import { useEffect, useMemo } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import UnprotectedRoutes from "../../routes/UnprotectedRoutes";
import { useStore } from "../providers/StoreContext";

const LOCAL_STORAGE_KEY = "iframeLastRoute";

const getIsInIframe = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

const getAllowedOrigins = () => {
  const raw = import.meta.env.VITE_IFRAME_ALLOWED_ORIGINS || "";
  return raw
    .split(",")
    .map((origin) => origin.trim())
    .filter(Boolean);
};

const isAllowedOrigin = (origin) => {
  const allowed = getAllowedOrigins();
  if (!allowed.length) return true;
  return allowed.includes(origin);
};

const normalizeRouteInput = (route) => {
  if (typeof route !== "string") return null;
  const trimmed = route.trim();
  if (!trimmed || trimmed === "null" || trimmed === "undefined") return null;

  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    try {
      const url = new URL(trimmed);
      if (url.origin !== window.location.origin) return null;
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return null;
    }
  }

  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
};

const getRouteParts = (route) => {
  if (!route) return null;
  try {
    const url = new URL(route, window.location.origin);
    return {
      pathname: url.pathname,
      full: `${url.pathname}${url.search}${url.hash}`,
    };
  } catch {
    return null;
  }
};

const isPublicRoute = (pathname) => {
  if (!pathname) return false;
  if (pathname === "/") return true;

  return UnprotectedRoutes.some((route) => {
    if (!route?.path || route.path === "*" || route.path === "/") return false;
    return !!matchPath({ path: route.path, end: true }, pathname);
  });
};

const hasAuthToken = () => {
  try {
    const token =
      localStorage.getItem("jwtToken") || localStorage.getItem("x-token");
    return !!token;
  } catch {
    return false;
  }
};

const DEFAULT_DASHBOARD_ROUTES = [
  "/admindashboard",
  "/customerdashboard",
  "/partnerdashboard",
  "/altairdashboard",
  "/dashboard",
];

const isDefaultDashboardRoute = (pathname) => {
  if (!pathname) return false;
  return DEFAULT_DASHBOARD_ROUTES.some((route) => pathname === route);
};

const requiresAuth = (pathname) => {
  if (!pathname) return false;
  return !isPublicRoute(pathname);
};

const IframeRouteManager = () => {
  const { store } = useStore();
  const location = useLocation();
  const navigate = useNavigate();
  const isInIframe = useMemo(() => getIsInIframe(), []);

  useEffect(() => {
    if (!isInIframe) return;

    const currentPathname = location.pathname || "/";
    const currentFullPath = `${location.pathname}${location.search}${location.hash}`;
    if (
      currentPathname &&
      typeof currentPathname === "string" &&
      currentPathname.trim() !== "" &&
      currentPathname !== "null" &&
      requiresAuth(currentPathname) &&
      hasAuthToken()
    ) {
      const savedRoute = normalizeRouteInput(
        localStorage.getItem(LOCAL_STORAGE_KEY)
      );
      // Avoid overwriting saved route with default dashboard after iframe reload.
      if (
        isDefaultDashboardRoute(currentPathname) &&
        savedRoute &&
        savedRoute !== currentFullPath
      ) {
        return;
      }
      localStorage.setItem(LOCAL_STORAGE_KEY, currentFullPath);
      return;
    }

    if (currentPathname === "/" || currentPathname.includes("/login")) {
      // Only clear when no auth token is present (avoid losing saved route on reload).
      if (!hasAuthToken()) {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
    }
  }, [
    isInIframe,
    location.pathname,
    location.search,
    location.hash,
  ]);

  useEffect(() => {
    if (!isInIframe) return;

    const currentPathname = location.pathname || "/";
    if (!isPublicRoute(currentPathname) && !isDefaultDashboardRoute(currentPathname)) return;

    const savedRoute = normalizeRouteInput(
      localStorage.getItem(LOCAL_STORAGE_KEY)
    );
    if (!savedRoute) return;

    const savedRouteParts = getRouteParts(savedRoute);
    if (!savedRouteParts) return;

    if (!hasAuthToken()) return;
    if (isPublicRoute(savedRouteParts.pathname)) return;

    const currentFullPath = `${location.pathname}${location.search}${location.hash}`;
    if (savedRouteParts.full !== currentFullPath) {
      setTimeout(() => {
        navigate(savedRouteParts.full, { replace: true });
      }, 50);
    }
  }, [
    isInIframe,
    location.pathname,
    location.search,
    location.hash,
    navigate,
  ]);

  useEffect(() => {
    if (!isInIframe) return;

    const handleMessage = (event) => {
      if (!event?.data || !isAllowedOrigin(event.origin)) return;
      if (event.data.type !== "SET_ROUTE" || !event.data.route) return;

      const normalized = normalizeRouteInput(event.data.route);
      const routeParts = getRouteParts(normalized);
      if (!routeParts) return;

      if (!isPublicRoute(routeParts.pathname) && !hasAuthToken()) return;
      navigate(routeParts.full);
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [isInIframe, navigate]);

  return null;
};

export default IframeRouteManager;
